import { AuthToken } from '@cobliteam/api-lib/src/account/session/AuthToken';
import { SessionManager } from '@cobliteam/api-lib/src/account/session/SessionManager';
import type { Session } from '@cobliteam/common-types';
import useAuthStore from '@cobliteam/hooks/src/useSession/store/useAuthStore';
import { Environment } from '@cobliteam/react-environments';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

const env = Environment.getInstance().getEnvironment();
const sessionManager = new SessionManager({ host: env.herbieApiHost });

export const SESSION_QUERY_KEY = 'session';

/**
 * Revalida a sessão do usuário contra `/account/session`. O `useAuthStore` (em memória) é a
 * fonte da verdade para renderização, e este hook o mantém em sincronia com o servidor.
 * Roda apenas quando existe refresh token.
 *
 * O `setSession` acontece dentro do `queryFn.then` (não via effect) para garantir atualização
 * atômica com a resposta — evita a render cycle extra do bridge react-query → Zustand no cold
 * boot, e permite que o `useUserData` gateado por `!!session` já enxergue a sessão fresca na
 * mesma render em que o response chega.
 */
const useSessionQuery = () => {
  const hasRefreshToken = !!localStorage.getItem(AuthToken.REFRESH_TOKEN);
  const hasAccessToken = !!localStorage.getItem(AuthToken.ACCESS_TOKEN);

  // Sem NENHUM token de auth, uma sessão que ainda esteja no store nunca será revalidada —
  // resultando em loop de redirect (AuthLayout → HOME, PrivateRouterProvider → /login).
  // Descarta o snapshot para que a app caia no fluxo público limpo.
  //
  // Só o access token presente (sem refresh) é um estado legítimo: `TemporaryTokenManager` seta
  // apenas ACCESS_TOKEN em impersonation de cobler. Nesse caso a sessão persistida é válida e
  // não pode ser limpa — senão o `PrivateRouterProvider` cai em `<LoadingPage>` eterno já que a
  // query fica `enabled: false` sem refresh token.
  useEffect(() => {
    if (!(hasRefreshToken || hasAccessToken) && useAuthStore.getState().session) {
      useAuthStore.getState().clearSession();
    }
  }, [hasRefreshToken, hasAccessToken]);

  const query = useQuery<Session>({
    queryKey: [SESSION_QUERY_KEY],
    queryFn: () =>
      sessionManager.getSession().then((session) => {
        useAuthStore.getState().setSession(session);
        return session;
      }),
    enabled: hasRefreshToken,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (query.error) useAuthStore.getState().clearSession();
  }, [query.error]);

  return query;
};

export default useSessionQuery;
